import { Carousel } from "../../controls/Carousel.js";
import { Expander } from "../../controls/Expander.js";
import { Grid } from "../../controls/Grid.js";
import { GroupBox } from "../../controls/GroupBox.js";
import { Page } from "../../controls/Page.js";
import { Panel } from "../../controls/Panel.js";
import { ScrollViewer } from "../../controls/ScrollViewer.js";
import { SplitContainer } from "../../controls/SplitContainer.js";
import { StackPanel } from "../../controls/StackPanel.js";
import { TabControl } from "../../controls/TabControl.js";
import { Window } from "../../controls/Window.js";
import type { Control } from "../../core/Control.js";
import type { Size } from "../../../common/geometry.js";
import { MarkupError } from "../errors.js";
import { registerElement, type Attrs, type BuildContext } from "../registry.js";
import { isAbsolute, toPixels, type Length } from "../values.js";
import type { XmlElement } from "../xml.js";

const ORIENTATIONS = ["horizontal", "vertical"] as const;

/** Options with the loader's own keys stripped, ready to hand to a constructor. */
const opts = <T>(attrs: Attrs): T => attrs as T;

export function registerContainers(): void {
  registerElement<Window>({
    tag: "Window",
    base: "container",
    docksChildren: true,
    attributes: {
      title: "string",
      min: "size",
      minWidth: "number",
      minHeight: "number",
      max: "size",
      toolWindow: "boolean",
      border: ["sizable", "single", "none"],
      resizable: "boolean",
      showMinimize: "boolean",
      showMaximize: "boolean",
      showClose: "boolean",
      sizeToContent: "boolean",
      center: "boolean",
      maximized: "boolean",
    },
    create: (attrs) => {
      const min = attrs["min"] as Size | undefined;
      const max = attrs["max"] as Size | undefined;
      return new Window({
        ...opts(attrs),
        ...(attrs["border"] ? { borderStyle: attrs["border"] as "sizable" } : {}),
        ...(min ? { minWidth: min.width, minHeight: min.height } : {}),
        ...(max ? { maximumSize: max } : {}),
      });
    },
    // A window's size is its own: an absent width means "keep the default", not "measure me",
    // because a window with no bounds of its own has nothing to be measured against.
    afterAttach: (win, attrs) => {
      if (attrs["sizeToContent"] === true) win.sizeToContent();
      if (attrs["center"] === true) {
        const area = win.desktop?.workArea;
        if (area) {
          win.setLocation(
            Math.round(area.x + (area.width - win.width) / 2),
            Math.round(area.y + (area.height - win.height) / 2),
          );
        }
      }
      if (attrs["maximized"] === true) win.maximize();
    },
  });

  registerElement<Page>({
    tag: "Page",
    base: "panel",
    docksChildren: true,
    attributes: { design: "size", fit: ["none", "contain", "width"] },
    create: (attrs) => new Page(opts(attrs)),
  });

  registerElement<Panel>({
    tag: "Panel",
    base: "panel",
    docksChildren: true,
    create: (attrs) => new Panel(opts(attrs)),
  });

  registerElement<GroupBox>({
    tag: "GroupBox",
    base: "panel",
    docksChildren: true,
    attributes: { text: "string", font: "font" },
    create: (attrs) => new GroupBox(opts(attrs)),
  });

  registerElement<Expander>({
    tag: "Expander",
    base: "panel",
    docksChildren: true,
    attributes: { text: "string", font: "font", expanded: "boolean", expandedHeight: "number" },
    create: (attrs) => new Expander(opts(attrs)),
  });

  registerElement<StackPanel>({
    tag: "StackPanel",
    aliases: ["VStack", "HStack"],
    base: "layout",
    attributes: { orientation: ORIENTATIONS, fillLast: "boolean" },
    create: (attrs, _ctx, element) =>
      new StackPanel({
        // The aliases are the whole reason to have them: `<HStack>` says its direction in its
        // name, where `orientation="horizontal"` says it in an attribute nobody reads twice.
        orientation: element.name === "HStack" ? "horizontal" : "vertical",
        ...opts<ConstructorParameters<typeof StackPanel>[0]>(attrs),
      }),
  });

  registerElement<Grid>({
    tag: "Grid",
    base: "layout",
    attributes: { rows: "tracks", cols: "tracks", columns: "tracks" },
    create: (attrs) =>
      new Grid({
        ...opts<ConstructorParameters<typeof Grid>[0]>(attrs),
        ...(attrs["cols"] ? { columns: attrs["cols"] as string[] } : {}),
      }),
  });

  registerElement<SplitContainer>({
    tag: "Split",
    aliases: ["SplitContainer"],
    base: "container",
    attributes: {
      orientation: ORIENTATIONS,
      position: "length",
      panel1Min: "number",
      panel2Min: "number",
      fixed: ["none", "panel1", "panel2"],
      splitterFixed: "boolean",
      collapsed: ["none", "panel1", "panel2"],
      splitterWidth: "number",
    },
    create: (attrs) =>
      new SplitContainer({
        ...opts<ConstructorParameters<typeof SplitContainer>[0]>(attrs),
        ...(attrs["fixed"] ? { fixedPane: attrs["fixed"] as "none" } : {}),
      }),
    /**
     * The two children are the two panes, in order.
     *
     * Written as ordinary children rather than as `<Pane>` wrappers, because a split with two
     * things in it is exactly what the markup should look like. Each pane fills, so whatever is
     * put in one needs no placement either.
     */
    addChild(split, child, element) {
      const filled = [split.panel1, split.panel2].filter((p) => p.controls.length > 0).length;
      if (filled >= 2) {
        throw new MarkupError(`a <Split> holds two children, one for each pane`, element);
      }
      const pane = filled === 0 ? split.panel1 : split.panel2;
      child.dock = "fill";
      pane.controls.add(child);
    },
    /**
     * The splitter position, last of all.
     *
     * A docked split has placeholder bounds until its parent has laid it out, so a position set
     * any earlier — in the options, at construction — is a fraction of a number that is not yet
     * true. The framework documents this trap for hand-written code; here it is simply handled.
     */
    afterAttach(split, attrs, ctx) {
      const length = attrs["position"] as Length | undefined;
      if (!length) return;
      const along = split.orientation === "vertical" ? split.width : split.height;
      if (length.kind === "percent") split.splitterPosition = Math.round(along * length.value);
      else if (isAbsolute(length)) {
        split.splitterPosition = Math.round(toPixels(length, ctx.theme.metrics));
      }
    },
  });

  registerElement<ScrollViewer>({
    tag: "Scroll",
    aliases: ["ScrollViewer"],
    base: "container",
    attributes: {
      zoom: "number",
      minZoom: "number",
      maxZoom: "number",
      zoomEnabled: "boolean",
      showBorder: "boolean",
      contentWidth: "number",
      contentHeight: "number",
    },
    create: (attrs) => new ScrollViewer(opts(attrs)),
    // Not `docksChildren`: content in a scroller keeps its own size, or there is nothing to
    // scroll. This is the one container where filling would defeat the control.
    addChild(viewer, child) {
      viewer.content.controls.add(child);
    },
  });

  registerElement<TabControl>({
    tag: "Tabs",
    aliases: ["TabControl"],
    base: "container",
    attributes: {
      placement: ["top", "bottom"],
      tabHeight: "number",
      closable: "boolean",
      selectedIndex: "number",
    },
    create: (attrs) => new TabControl(opts(attrs)),
    childSpecs: {
      Tab(element, ctx, tabs) {
        const attrs = ctx.attributesOf(element, {
          text: "string",
          closable: "boolean",
          tooltip: "string",
          name: "string",
        });
        const page = tabs.addPage((attrs["text"] as string) ?? element.name, {
          ...(attrs["closable"] === undefined ? {} : { closable: attrs["closable"] as boolean }),
          ...(attrs["tooltip"] === undefined ? {} : { tooltip: attrs["tooltip"] as string }),
        });
        fillPage(page, element, ctx);
      },
    },
    afterAttach(tabs, attrs) {
      const index = attrs["selectedIndex"];
      if (typeof index === "number") tabs.selectedIndex = index;
    },
  });

  registerElement<Carousel>({
    tag: "Carousel",
    base: "container",
    attributes: { selectedIndex: "number", showDots: "boolean", swipe: "boolean" },
    create: (attrs) => new Carousel(opts(attrs)),
    childSpecs: {
      Slide(element, ctx, carousel) {
        const attrs = ctx.attributesOf(element, { tooltip: "string" });
        fillPage(
          carousel.addPage(
            attrs["tooltip"] === undefined ? {} : { tooltip: attrs["tooltip"] as string },
          ),
          element,
          ctx,
        );
      },
    },
  });
}

/**
 * Put a `<Tab>` or `<Slide>`'s children into the panel the control handed back.
 *
 * The panel is owned by its tab control, so it is never built from markup itself — but its
 * children should behave exactly as they would in any other container, filling by default.
 */
function fillPage(page: { controls: { add(c: Control): unknown } }, element: XmlElement, ctx: BuildContext): void {
  for (const child of element.children) {
    const built = ctx.build(child, null);
    if (
      built.dock === "none" &&
      built.hAlign === null &&
      built.vAlign === null &&
      built.x === 0 &&
      built.y === 0
    ) {
      built.dock = "fill";
    }
    page.controls.add(built);
  }
}
